/**
 * Parked payment events (TMMPDM-0071, tmm-api 0121): the pay-api feed events tmm-api couldn't apply —
 * a refund at once when applying it would be unsafe, anything else after three failures. An admin
 * Re-applies one (tmm-api fetches it from pay-api and applies it once) or Dismisses it, each with a
 * note. The list is server-paged (`GET /v1/admin/billing/parked-events`), newest parked first; the tab
 * (`?resolved=true`) and the page live in the URL (TMM-B6). The page's h1 and title render at once,
 * before the list loads (TMM-B7).
 *
 * Nothing here decides anything from two fields: each cell shows one thing the server sent. An unsent
 * value is "—", `exceptionClass` is never shown (it is copied for support), and a re-read follows every
 * action and refusal. The list keeps showing its rows while it re-reads, so nothing under the pointer
 * or the keyboard moves; when the row a person acted on leaves the list, focus goes to the table.
 *
 * TMMPDM-0072: a refused re-apply changes nothing at tmm-api, so the row keeps its stored reason and
 * shows the new one beneath it — "Last re-apply refused just now" — from client state keyed by `seq`
 * (this visit only, nothing stored). The pager is hidden when there is nothing to page.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, positiveInt, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { adminApi } from '@/api/tmm/admin';
import { isActingAdminRevoked } from '@/api/errors';
import { EmptyState, ErrorState, PageHeader, useNotify } from '@/components/page';
import { FilterTabs, stickyTableContainerSx } from '@/components/ui';
import { formatInstant, formatNumber } from '@/format';
import { StatusChip } from '@/status';
import type { ParkedFeedEventPage, ParkedFeedEventView } from '@/api/tmm/types';
import { ParkedEventDialog, type ParkedAction } from './ParkedEventDialog';
import { eventTypeWords, reasonWords, refusalReason, supportDetails } from './parkedEventCopy';

const PAGE_SIZE = 20;

/** TMMPDM-0071: the tab (`resolved`) and the 1-based `page` live in the URL; Unresolved is the default. */
type ParkedView = { resolved: 'false' | 'true'; page: number };
const VIEW: UrlSchema<ParkedView> = {
  resolved: { default: 'false', parse: oneOf(['false', 'true'] as const) },
  page: { default: 1, parse: positiveInt },
};

interface DialogState {
  seq: number;
  mode: ParkedAction;
  open: boolean;
  /** A new opening gets a fresh note and no old error. */
  nonce: number;
}

const headerCellSx = { fontWeight: 600 } as const;
/** An identifier: the mono face, breaking anywhere so a long id wraps inside its column. */
const monoSx: SxProps<Theme> = (theme) => ({ ...theme.typography.mono, wordBreak: 'break-all' });

export function ParkedEvents() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const notify = useNotify();
  const [view, setView] = useUrlState(VIEW);
  const resolvedTab = view.resolved === 'true';
  const page = view.page - 1;
  const [reloadToken, setReloadToken] = useState(0);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  /** The reason code of the last refused re-apply this visit, by `seq`. Not stored; the row's own reason is the server's. */
  const [lastRefusal, setLastRefusal] = useState<Readonly<Record<number, string>>>({});
  const viewKey = `${view.resolved}:${page}`;
  const requestKey = `${viewKey}:${reloadToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    viewKey: string | null;
    result: ParkedFeedEventPage | null;
    failure: unknown;
  }>({ requestKey: null, viewKey: null, result: null, failure: null });
  const tableRef = useRef<HTMLDivElement>(null);
  /** Set when a row may have left the list: focus goes to the table once the dialog and the re-read are done. */
  const refocus = useRef(false);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listParkedEvents({ resolved: resolvedTab, page, size: PAGE_SIZE })
      .then((result) => {
        if (!cancelled) setResponse({ requestKey, viewKey, result, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled)
          setResponse({ requestKey, viewKey, result: null, failure: failure ?? 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [resolvedTab, page, requestKey, viewKey]);

  const fresh = response.requestKey === requestKey;
  // A re-read of the same view keeps the rows it has; a different view (tab, page) shows nothing yet.
  const showing = response.viewKey === viewKey ? response.result : null;
  const loading = !fresh && showing === null;
  const refreshing = !fresh && showing !== null;
  const failure = fresh ? response.failure : null;
  const rows = showing?.events ?? [];
  const totalPages = showing?.totalPages ?? 0;
  const pageNumber = (showing?.page ?? page) + 1;
  const reread = () => setReloadToken((n) => n + 1);

  useEffect(() => {
    if (!refocus.current || dialog?.open || !fresh) return;
    refocus.current = false;
    // The row a person acted on may have left the list, and its button with it: focus is then on <body>,
    // or still on a dialog button that is about to unmount. Where it has moved on (to the opener, which
    // is still there, or anywhere the person chose), leave it.
    const active = document.activeElement;
    if (active === null || active === document.body || active.closest('[role="dialog"]')) {
      tableRef.current?.focus({ preventScroll: true });
    }
  }, [dialog, fresh]);

  const openDialog = (seq: number, mode: ParkedAction) =>
    setDialog((current) => ({ seq, mode, open: true, nonce: (current?.nonce ?? 0) + 1 }));
  const closeDialog = () => setDialog((current) => (current ? { ...current, open: false } : null));

  const copySupport = async (event: ParkedFeedEventView) => {
    try {
      await navigator.clipboard.writeText(supportDetails(event));
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
    }
  };

  const copyId = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
    }
  };

  const unsent = '—';
  const mono = (value: string | null): ReactNode =>
    value === null || value === '' ? (
      unsent
    ) : (
      <Box component="code" sx={monoSx}>
        {value}
      </Box>
    );
  const columns = resolvedTab ? 11 : 7;
  // A page past the end (a stale or hand-edited link) is narrowing too: Clear goes back to page 1.
  const empty = (
    <EmptyState
      title={
        resolvedTab
          ? t('parkedEvents.emptyResolved', 'Nothing resolved yet.')
          : t('parkedEvents.empty', 'No parked payment events.')
      }
      {...(view.page > 1 ? { filtered: { onClear: () => setView({ page: 1 }) } } : {})}
    />
  );

  return (
    <Box>
      <PageHeader
        title={t('page.parkedEvents.title', 'Parked payment events')}
        subtitle={t(
          'page.parkedEvents.subtitle',
          "Payment events tmm-api couldn't apply. Re-apply one once the cause is fixed, or dismiss it.",
        )}
        meta={
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ alignItems: 'center', mt: 0.5, color: 'text.secondary' }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
            <Typography variant="body2">
              {t('parkedEvents.retention', 'Re-apply works for 90 days after the payment event.')}
            </Typography>
          </Stack>
        }
      />

      <Box sx={{ mb: 2 }}>
        <FilterTabs
          ariaLabel={t('parkedEvents.tabsAria', 'Parked payment events by resolution')}
          active={view.resolved}
          onChange={(key) => setView({ resolved: key === 'true' ? 'true' : 'false' })}
          tabs={[
            { key: 'false', label: t('parkedEvents.tab.unresolved', 'Unresolved') },
            { key: 'true', label: t('parkedEvents.tab.resolved', 'Resolved') },
          ]}
        />
      </Box>

      {failure ? (
        <ErrorState
          error={failure}
          message={t('parkedEvents.loadError', 'Could not load parked payment events.')}
          {...(isActingAdminRevoked(failure) ? {} : { onRetry: reread })}
        />
      ) : (
        <TableContainer
          ref={tableRef}
          component={Paper}
          variant="outlined"
          role="region"
          aria-label={t('parkedEvents.regionAria', 'Parked payment events list')}
          aria-busy={loading || refreshing ? 'true' : 'false'}
          tabIndex={-1}
          sx={stickyTableContainerSx}
        >
          <Table
            size="small"
            stickyHeader
            aria-label={t('parkedEvents.tableAria', 'Parked payment events')}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>{t('parkedEvents.col.parked', 'Parked')}</TableCell>
                <TableCell sx={headerCellSx}>{t('parkedEvents.col.event', 'Event')}</TableCell>
                <TableCell sx={headerCellSx}>{t('parkedEvents.col.reason', 'Reason')}</TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('parkedEvents.col.attempts', 'Attempts')}
                </TableCell>
                <TableCell sx={headerCellSx}>{t('parkedEvents.col.payment', 'Payment')}</TableCell>
                <TableCell sx={headerCellSx}>
                  {t('parkedEvents.col.subscription', 'Subscription')}
                </TableCell>
                {resolvedTab ? (
                  <>
                    <TableCell sx={headerCellSx}>
                      {t('parkedEvents.col.resolution', 'Resolution')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      {t('parkedEvents.col.resolved', 'Resolved')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>{t('parkedEvents.col.by', 'By')}</TableCell>
                    <TableCell sx={headerCellSx}>{t('parkedEvents.col.note', 'Note')}</TableCell>
                  </>
                ) : null}
                <TableCell sx={headerCellSx}>{t('parkedEvents.col.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('common.loading', 'Loading')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns}>{empty}</TableCell>
                </TableRow>
              ) : (
                rows.map((event) => {
                  const words = eventTypeWords(event.eventType, t);
                  return (
                    <TableRow key={event.seq} hover>
                      <TableCell>{formatInstant(event.parkedAt, locale)}</TableCell>
                      <TableCell>{words ?? mono(event.eventType)}</TableCell>
                      <TableCell>
                        {reasonWords(event.reasonCode, t)}
                        {resolvedTab || lastRefusal[event.seq] === undefined ? null : (
                          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            {t(
                              'parkedEvents.lastRefusal',
                              'Last re-apply refused just now: {{reason}}',
                              { reason: refusalReason(lastRefusal[event.seq] ?? '', t, locale) },
                            )}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{formatNumber(event.attempts, locale)}</TableCell>
                      <TableCell>
                        {event.paymentId === null || event.paymentId === '' ? (
                          unsent
                        ) : (
                          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                            <Box component="code" sx={monoSx}>
                              {event.paymentId}
                            </Box>
                            <IconButton
                              size="small"
                              aria-label={t('components.copy', 'Copy {{identifier}}', {
                                identifier: event.paymentId,
                              })}
                              onClick={() => void copyId(event.paymentId ?? '')}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>{mono(event.subscriptionId)}</TableCell>
                      {resolvedTab ? (
                        <>
                          <TableCell>
                            {event.resolution === null ? (
                              unsent
                            ) : (
                              <StatusChip
                                status={event.resolution}
                                label={t(
                                  `parkedEvents.resolution.${event.resolution}`,
                                  event.resolution,
                                )}
                              />
                            )}
                          </TableCell>
                          <TableCell>{formatInstant(event.resolvedAt, locale)}</TableCell>
                          <TableCell sx={{ overflowWrap: 'anywhere' }}>
                            {event.resolvedBy === null || event.resolvedBy === ''
                              ? unsent
                              : event.resolvedBy}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280, overflowWrap: 'anywhere' }}>
                            {event.resolutionNote === null || event.resolutionNote === ''
                              ? unsent
                              : event.resolutionNote}
                          </TableCell>
                        </>
                      ) : null}
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
                        >
                          {resolvedTab ? null : (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                aria-label={t(
                                  'parkedEvents.reapply.rowAria',
                                  'Re-apply event {{seq, number}}',
                                  { seq: event.seq },
                                )}
                                onClick={() => openDialog(event.seq, 'reapply')}
                              >
                                {t('parkedEvents.reapply.confirm', 'Re-apply')}
                              </Button>
                              <Button
                                size="small"
                                aria-label={t(
                                  'parkedEvents.dismiss.rowAria',
                                  'Dismiss event {{seq, number}}',
                                  { seq: event.seq },
                                )}
                                onClick={() => openDialog(event.seq, 'dismiss')}
                              >
                                {t('parkedEvents.dismiss.confirm', 'Dismiss')}
                              </Button>
                            </>
                          )}
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon fontSize="small" />}
                            aria-label={t(
                              'parkedEvents.support.rowAria',
                              'Copy details for support for event {{seq, number}}',
                              { seq: event.seq },
                            )}
                            onClick={() => void copySupport(event)}
                          >
                            {t('parkedEvents.support.label', 'Details for support')}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {loading || rows.length > 0 ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 2, alignItems: 'center', justifyContent: 'flex-end' }}
        >
          <Button
            variant="outlined"
            disabled={loading || (showing?.page ?? page) <= 0}
            onClick={() => setView({ page: Math.max(1, view.page - 1) })}
          >
            {t('tenants.pagination.previous', 'Previous')}
          </Button>
          <Typography variant="body2" aria-live="polite">
            {totalPages === 0
              ? null
              : t('tenants.pagination.pageOf', 'Page {{page, number}} of {{total, number}}', {
                  page: pageNumber,
                  total: totalPages,
                })}
          </Typography>
          <Button
            variant="outlined"
            disabled={loading || totalPages === 0 || (showing?.page ?? page) + 1 >= totalPages}
            onClick={() => setView({ page: view.page + 1 })}
          >
            {t('tenants.pagination.next', 'Next')}
          </Button>
        </Stack>
      ) : null}

      {dialog ? (
        <ParkedEventDialog
          key={dialog.nonce}
          open={dialog.open}
          seq={dialog.seq}
          mode={dialog.mode}
          onClose={closeDialog}
          onStillRefused={(reasonCode) =>
            setLastRefusal((current) => ({ ...current, [dialog.seq]: reasonCode }))
          }
          onResolved={() => {
            refocus.current = true;
            closeDialog();
            reread();
          }}
          onChanged={() => {
            refocus.current = true;
            reread();
          }}
          onDismissInstead={() =>
            setDialog((current) => (current ? { ...current, mode: 'dismiss' } : null))
          }
        />
      ) : null}
    </Box>
  );
}
